import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enterprisesApi } from '@/api/enterprisesApi';
import type {
  CompleteEnterpriseSignupRequest,
  CreateEnterpriseRequest,
  EnterpriseListRequest,
  GenerateSignupUrlRequest,
  UpdateEnterpriseRequest,
} from '@/types/enterprise';

const QUERY_KEYS = {
  all: ['enterprises'] as const,
  list: (filters: EnterpriseListRequest) => [...QUERY_KEYS.all, 'list', filters] as const,
  detail: (id: number) => [...QUERY_KEYS.all, 'detail', id] as const,
};

export function useEnterprises(filters: EnterpriseListRequest = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.list(filters),
    queryFn: () => enterprisesApi.list(filters),
  });
}

export function useEnterprise(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.detail(id ?? 0),
    queryFn: () => enterprisesApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateEnterpriseRequest) => enterprisesApi.create(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
}

export function useUpdateEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateEnterpriseRequest }) =>
      enterprisesApi.update(id, request),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.all });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.id) });
    },
  });
}

export function useDeactivateEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => enterprisesApi.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
}

export function useReactivateEnterprise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => enterprisesApi.reactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
}

// === Signup flow ===

export function useGenerateSignupUrl() {
  return useMutation({
    mutationFn: (request: GenerateSignupUrlRequest) => enterprisesApi.generateSignupUrl(request),
  });
}

export function useCompleteEnterpriseSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CompleteEnterpriseSignupRequest) => enterprisesApi.completeSignup(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.all });
    },
  });
}
